import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API, useAuth } from "../App";
import axios from "axios";
import V11ChapterContent from "../components/V11ChapterContent";
import { useTranslation } from "react-i18next";
import {
  BookOpen, Search, ChevronRight, ChevronLeft, Loader2,
  Bookmark, BookmarkCheck, CheckCircle2, ArrowLeft, List,
  Lightbulb, FlaskConical, AlertTriangle, ExternalLink,
  GraduationCap, Hash, Library, Layers, Beaker, HelpCircle,
  FileText, BookMarked, Table2
} from "lucide-react";

/* ─── Views ───────────────────────────────────────────────────── */
const VIEW_HOME = "home";
const VIEW_BOOK = "book";
const VIEW_READ = "read";
const VIEW_SEARCH = "search";
const VIEW_S3_VOLUMES = "s3_volumes";
const VIEW_S3_VOLUME = "s3_volume";
const VIEW_S3_NODE = "s3_node";

const CATEGORY_LABELS = {
  chapter: "Chapters",
  case_study: "Case Studies",
  lab_simulation: "Lab Simulations",
  qa: "Study Questions",
  quick_reference: "Quick Reference",
  foreword: "Foreword",
  appendix: "Appendix",
};

const CATEGORY_ICONS = {
  chapter: BookOpen,
  case_study: FileText,
  lab_simulation: Beaker,
  qa: HelpCircle,
  quick_reference: BookMarked,
  foreword: BookOpen,
  appendix: BookMarked,
};

const CATEGORY_COLORS = {
  chapter: "border-[#1A6BCC]/30 bg-[#1A6BCC]/5",
  case_study: "border-amber-500/30 bg-amber-500/5",
  lab_simulation: "border-emerald-500/30 bg-emerald-500/5",
  qa: "border-purple-500/30 bg-purple-500/5",
  quick_reference: "border-cyan-500/30 bg-cyan-500/5",
  foreword: "border-slate-500/20 bg-slate-500/5",
  appendix: "border-slate-500/20 bg-slate-500/5",
};

/**
 * Resolve the v11 "Part" label for a given chapter/node, using the
 * 3-part architecture returned by /api/reader-hub/v11/architecture/{book_id}.
 * Returns the authored Part label (e.g. "Part I — Statistical and Engineering
 * Foundations") when a match is found, otherwise null so callers can fall
 * back to legacy display strings.
 *
 * Lookup priority: node_id exact match → chapter number match.
 */
const resolvePartFromArch = (arch, target) => {
  if (!arch || arch._error || !Array.isArray(arch.parts) || !target) return null;
  const nodeId = target.node_id;
  const chapterNum = target.chapter ?? target.chapter_num;
  for (const part of arch.parts) {
    for (const ch of part.chapters || []) {
      if (nodeId && ch.node_id === nodeId) return part;
      if (chapterNum != null && ch.chapter === chapterNum) return part;
    }
  }
  return null;
};

/** Short display form ("Part I", "Part II", "Part III") for compact strips. */
const shortPartLabel = (part) => {
  if (!part) return null;
  const roman = { 1: "I", 2: "II", 3: "III" }[part.part] || part.part;
  return `Part ${roman}`;
};

const ReaderDashboardPage = () => {
  const { t } = useTranslation('platform');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const justActivated = location.state?.justActivated;
  const initialBookId = location.state?.bookId;

  const [view, setView] = useState(VIEW_HOME);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  /* Series I/II state */
  const [selectedBook, setSelectedBook] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [chapterLoading, setChapterLoading] = useState(false);

  /* Series III state */
  const [s3Book, setS3Book] = useState(null);
  const [s3Volume, setS3Volume] = useState(null);
  const [s3VolumeData, setS3VolumeData] = useState(null);
  const [s3Node, setS3Node] = useState(null);
  const [s3NodeLoading, setS3NodeLoading] = useState(false);
  // v11 Book Architecture (3 Parts) — replaces the legacy 4-volume grouping
  // on the reader-facing surface. Source: /api/reader-hub/v11/architecture/{book_id}.
  const [v11Arch, setV11Arch] = useState(null);
  const [v11ArchLoading, setV11ArchLoading] = useState(false);

  /* Search */
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const contentRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reader-hub/dashboard`, { withCredentials: true });
      setDashboard(res.data);
      if (initialBookId && res.data.books?.length) {
        const book = res.data.books.find(b => b.book_id === initialBookId);
        if (book) {
          if (book.is_series_iii) {
            setS3Book(book);
            setView(VIEW_S3_VOLUMES);
          } else {
            setSelectedBook(book);
            setView(VIEW_BOOK);
          }
        }
      }
    } catch {
      navigate("/readers");
    } finally {
      setLoading(false);
    }
  }, [navigate, initialBookId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  /* ── Series I/II handlers ── */
  const openBook = (book) => {
    if (book.is_series_iii) {
      setS3Book(book);
      setView(VIEW_S3_VOLUMES);
    } else {
      setSelectedBook(book);
      setView(VIEW_BOOK);
    }
  };

  const readChapter = async (bookId, chapterNum) => {
    setChapterLoading(true);
    try {
      const res = await axios.get(`${API}/reader-hub/content/${bookId}/chapter/${chapterNum}`, { withCredentials: true });
      setChapter(res.data);
      setView(VIEW_READ);
      contentRef.current?.scrollTo(0, 0);
    } catch (err) {
      console.error("Failed to load chapter:", err);
    } finally {
      setChapterLoading(false);
    }
  };

  const toggleBookmark = async (bookId, chapterNum) => {
    try {
      await axios.post(`${API}/reader-hub/progress/${bookId}/bookmark`, { chapter_num: chapterNum }, { withCredentials: true });
      fetchDashboard();
    } catch {}
  };

  const markComplete = async (bookId, chapterNum) => {
    try {
      await axios.post(`${API}/reader-hub/progress/${bookId}/complete`, { chapter_num: chapterNum }, { withCredentials: true });
      fetchDashboard();
    } catch {}
  };

  /* ── Series III handlers ── */
  const loadV11Architecture = useCallback(async (bookId) => {
    setV11ArchLoading(true);
    setV11Arch(null);
    try {
      const res = await axios.get(
        `${API}/reader-hub/v11/architecture/${bookId}`,
        { withCredentials: true }
      );
      setV11Arch(res.data);
    } catch (err) {
      console.error("Failed to load v11 architecture:", err);
      setV11Arch({ _error: err.response?.data?.detail || err.message });
    } finally {
      setV11ArchLoading(false);
    }
  }, []);

  useEffect(() => {
    // Eager-load v11 architecture for any Series III book in the dashboard
    // so search-hit metadata (and any deep-linked node view) can render
    // Part labels immediately — without requiring the user to first
    // navigate into the book's volumes view.
    if (!dashboard || v11Arch || v11ArchLoading) return;
    const s3 = dashboard.books?.find(b => b.is_series_iii);
    if (s3) {
      loadV11Architecture(s3.book_id);
    }
  }, [dashboard, v11Arch, v11ArchLoading, loadV11Architecture]);

  useEffect(() => {
    if (s3Book && view === VIEW_S3_VOLUMES && !v11Arch) {
      loadV11Architecture(s3Book.book_id);
    }
  }, [s3Book, view, v11Arch, loadV11Architecture]);

  const openVolume = async (volNum) => {
    if (!s3Book) return;
    setS3Volume(volNum);
    setS3VolumeData(null);
    setView(VIEW_S3_VOLUME);
    try {
      const res = await axios.get(`${API}/reader-hub/content-s3/${s3Book.book_id}/volume/${volNum}/browse`, { withCredentials: true });
      setS3VolumeData(res.data);
    } catch (err) {
      console.error("Failed to load volume:", err);
    }
  };

  const readS3Node = async (nodeId) => {
    if (!s3Book) return;
    setS3NodeLoading(true);
    try {
      const vol = s3Volume || 1;
      const res = await axios.get(`${API}/reader-hub/content-s3/${s3Book.book_id}/volume/${vol}/node/${nodeId}`, { withCredentials: true });
      setS3Node(res.data);
      setView(VIEW_S3_NODE);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Failed to load node:", err);
    } finally {
      setS3NodeLoading(false);
    }
  };

  /* ── Search ── */
  const doSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/reader-hub/search`, { params: { q: searchQuery }, withCredentials: true });
      setSearchResults(res.data.results || []);
      setView(VIEW_SEARCH);
    } catch {}
    setSearching(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" />
      </div>
    );
  }

  /* ═══ SERIES III NODE READING VIEW ═══ */
  if (view === VIEW_S3_NODE && s3Node) {
    const CatIcon = CATEGORY_ICONS[s3Node.category] || BookOpen;

    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="s3-node-view">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => { setS3Node(null); setView(VIEW_S3_VOLUME); }}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-volume"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Volume
          </button>
          <div className="text-xs text-slate-500 font-mono">{s3Node.node_id}</div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="text-[10px] text-[#00C8E0] uppercase tracking-widest font-bold mb-2 flex items-center gap-2" data-testid="s3-node-part-strip">
              <CatIcon className="w-3.5 h-3.5" />
              {(() => {
                // v11 Architecture migration (Items 6-7): render the Part
                // label from /api/reader-hub/v11/architecture/{book_id}
                // instead of the legacy 4-volume `volume_title` string.
                // Falls back to the legacy label if the architecture lookup
                // fails, so readers never see an empty strip.
                const part = resolvePartFromArch(v11Arch, s3Node);
                const partLabel = part
                  ? (part.label || shortPartLabel(part))
                  : s3Node.volume_title;
                const catLabel = s3Node.category?.replace("_", " ");
                return `${partLabel} · ${catLabel}`;
              })()}
            </div>
            {s3Node.chapter_title && (
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="s3-node-chapter-title">{s3Node.chapter_title}</h1>
            )}
            {s3Node.section_title && s3Node.section_title !== s3Node.chapter_title && (
              <h2 className="text-lg text-slate-300 mb-2">{s3Node.section_title}</h2>
            )}
            {s3Node.topic && s3Node.topic !== s3Node.section_title && s3Node.topic !== s3Node.chapter_title && (
              <p className="text-sm text-slate-400">{s3Node.topic}</p>
            )}
          </div>

          {/* Published Content */}
          {s3Node.content && (
            <div className="mb-8" data-testid="s3-node-content">
              <V11ChapterContent
                markdown={s3Node.content}
                chapterNum={s3Node.chapter}
              />
            </div>
          )}

          {/* Tables */}
          {s3Node.tables?.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Table2 className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Reference Tables ({s3Node.tables.length})</span>
              </div>
              {s3Node.tables.map((tbl, i) => (
                <div key={i} className="text-xs font-mono text-slate-300 bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-lg px-4 py-3 mb-3 overflow-x-auto whitespace-pre-wrap" data-testid={`s3-table-${i}`}>
                  {tbl}
                </div>
              ))}
            </div>
          )}

          {/* Watermark */}
          <div className="text-center text-[10px] text-slate-600 py-4 border-t border-[#1E3A5F] mt-8" data-testid="reader-watermark">
            {s3Node.watermark}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ SERIES III VOLUME BROWSE VIEW ═══ */
  if (view === VIEW_S3_VOLUME && s3Volume) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="s3-volume-view">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => { setS3Volume(null); setS3VolumeData(null); setView(VIEW_S3_VOLUMES); }}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-volumes"
          >
            <ArrowLeft className="w-4 h-4" /> All Volumes
          </button>
          {s3VolumeData && (
            <div className="text-xs text-slate-500">{s3VolumeData.total_nodes} sections</div>
          )}
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          {!s3VolumeData ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 text-[#1A6BCC] animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="text-[10px] text-[#00C8E0] uppercase tracking-widest font-bold mb-2">Volume {s3Volume}</div>
                <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="s3-volume-title">{s3VolumeData.title}</h1>
                <p className="text-slate-400 text-sm">{s3VolumeData.subtitle}</p>
              </div>

              {/* Content groups by category */}
              <div className="space-y-6" data-testid="s3-volume-groups">
                {/* Prioritize chapter first, then case_study, lab_simulation, qa, quick_reference, foreword */}
                {["chapter", "case_study", "lab_simulation", "qa", "quick_reference", "foreword", "appendix"].map(cat => {
                  const nodes = s3VolumeData.groups[cat];
                  if (!nodes || nodes.length === 0) return null;
                  const CatIcon = CATEGORY_ICONS[cat] || BookOpen;
                  const catStyle = CATEGORY_COLORS[cat] || "border-slate-500/20";

                  return (
                    <div key={cat} className={`border rounded-lg overflow-hidden ${catStyle}`} data-testid={`s3-group-${cat}`}>
                      <div className="px-4 py-3 flex items-center gap-2 border-b border-inherit">
                        <CatIcon className="w-4 h-4 text-slate-300" />
                        <span className="text-sm font-bold text-white">{CATEGORY_LABELS[cat] || cat}</span>
                        <span className="text-[10px] text-slate-500 ml-auto">{nodes.length}</span>
                      </div>
                      <div className="divide-y divide-inherit">
                        {nodes.map((n, ni) => (
                          <button
                            key={ni}
                            onClick={() => readS3Node(n.node_id)}
                            disabled={s3NodeLoading}
                            className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors disabled:opacity-50"
                            data-testid={`s3-node-btn-${n.node_id}`}
                          >
                            <div className="w-6 h-6 rounded bg-[#132340] flex items-center justify-center text-[10px] text-slate-400 font-mono shrink-0">
                              {n.section || ni + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-white truncate">{n.section_title || n.topic || n.chapter_title}</div>
                              {n.chapter_title && cat === "chapter" && n.section_title && (
                                <div className="text-[10px] text-slate-500 truncate">{n.chapter_title}</div>
                              )}
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ═══ SERIES III PART-GROUPED VIEW (v11 Book Architecture) ═══ */
  if (view === VIEW_S3_VOLUMES && s3Book) {
    // P0.3: bilingual labels moved to reader.json i18n bundle
    const tr = (key) => t(`v11.s3_view.${key}`);
    const L = {
      backHome: tr("back_home"),
      seriesTag: tr("series_tag"),
      frontMatter: tr("front_matter"),
      backMatter: tr("back_matter"),
      chapter: tr("chapter"),
      loadingArch: tr("loading_architecture"),
      archError: tr("architecture_error"),
      threePartsCaption: tr("three_parts_caption"),
    };
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="s3-parts-overview">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => { setS3Book(null); setV11Arch(null); setView(VIEW_HOME); }}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" /> {L.backHome}
          </button>
          {v11Arch && !v11Arch._error && (
            <div className="text-xs text-slate-500" data-testid="s3-chapter-count">
              {v11Arch.total_chapters} chapters
            </div>
          )}
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* Book header */}
          <div className="mb-8">
            <div className="text-[10px] text-[#00C8E0] uppercase tracking-widest font-bold mb-2">
              {L.seriesTag}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="s3-book-title">
              {v11Arch?.title || s3Book.title}
            </h1>
            <p className="text-slate-400 text-sm">
              {L.threePartsCaption}
            </p>
            <div className="mt-3 text-xs text-slate-500">{s3Book.author}</div>
          </div>

          {v11ArchLoading && (
            <div className="text-sm text-slate-500 text-center py-8">{L.loadingArch}</div>
          )}

          {v11Arch?._error && (
            <div className="bg-rose-900/10 border border-rose-800/30 rounded-lg p-4 text-rose-300 text-sm">
              {L.archError} <span className="text-slate-400">({v11Arch._error})</span>
            </div>
          )}

          {v11Arch && !v11Arch._error && (
            <div className="space-y-6" data-testid="s3-parts-list">
              {/* Front matter — renders above Part I (per spec) */}
              {v11Arch.front_matter && (
                <BoundaryMatterCard
                  node={v11Arch.front_matter}
                  label={L.frontMatter}
                  onClick={() => readS3Node(v11Arch.front_matter.node_id)}
                  testid="s3-front-matter"
                />
              )}

              {v11Arch.parts.map((part) => (
                <PartSection
                  key={part.part}
                  part={part}
                  chapterLabel={L.chapter}
                  onChapterClick={(nodeId) => readS3Node(nodeId)}
                />
              ))}

              {/* Back matter — renders below Part III (per spec) */}
              {v11Arch.back_matter && (
                <BoundaryMatterCard
                  node={v11Arch.back_matter}
                  label={L.backMatter}
                  onClick={() => readS3Node(v11Arch.back_matter.node_id)}
                  testid="s3-back-matter"
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ═══ SERIES I/II READING VIEW — Full Chapter ═══ */
  if (view === VIEW_READ && chapter) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" ref={contentRef} data-testid="reader-chapter-view">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => { setChapter(null); setView(VIEW_BOOK); }}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-book"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Contents
          </button>
          <div className="text-xs text-slate-500 font-mono">
            Ch. {chapter.chapter_num} / {chapter.total_chapters}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => toggleBookmark(chapter.book_id, chapter.chapter_num)}
              className="text-slate-400 hover:text-amber-400 transition-colors"
              data-testid="bookmark-btn"
            >
              <Bookmark className="w-4 h-4" />
            </button>
            <button
              onClick={() => markComplete(chapter.book_id, chapter.chapter_num)}
              className="text-slate-400 hover:text-emerald-400 transition-colors"
              data-testid="mark-complete-btn"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* Chapter header */}
          <div className="mb-8">
            <div className="text-[10px] text-[#00C8E0] uppercase tracking-widest font-bold mb-2">{chapter.category}</div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-3" data-testid="chapter-title">
              Chapter {chapter.chapter_num}: {chapter.chapter_title}
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed">{chapter.abstract}</p>
          </div>

          {/* Sections */}
          {chapter.sections?.map((sec, si) => (
            <div key={si} className="mb-10" data-testid={`section-${si + 1}`}>
              {chapter.sections.length > 1 && (
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2 border-b border-[#1E3A5F]/50 pb-2">
                  <Hash className="w-4 h-4 text-[#1A6BCC]" />
                  Section {sec.section_num}: {sec.title}
                </h2>
              )}

              {sec.intro && <p className="text-slate-300 text-sm leading-relaxed mb-6">{sec.intro}</p>}

              {sec.concepts?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Key Concepts</span>
                  </div>
                  <div className="space-y-2">
                    {sec.concepts.map((c, ci) => (
                      <div key={ci} className="bg-[#0D1F38] border border-[#1E3A5F]/50 rounded px-4 py-3 text-sm text-slate-300 leading-relaxed">{c}</div>
                    ))}
                  </div>
                </div>
              )}

              {sec.formulas?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <FlaskConical className="w-4 h-4 text-[#00C8E0]" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Formulas</span>
                  </div>
                  <div className="space-y-3">
                    {sec.formulas.map((f, fi) => (
                      <div key={fi} className="bg-[#132340] border border-[#1E3A5F] rounded p-4">
                        <div className="text-xs font-bold text-[#00C8E0] mb-1">{f.name}</div>
                        <div className="text-white text-sm font-mono" dangerouslySetInnerHTML={{ __html: f.formula }} />
                        <div className="text-slate-500 text-[10px] mt-1">{f.standard}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sec.worked_example && (
                <div className="mb-6 bg-[#0D1F38] border border-emerald-800/30 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="w-4 h-4 text-emerald-400" />
                    <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Worked Example</span>
                  </div>
                  <h4 className="text-sm font-bold text-white mb-2">{sec.worked_example.title}</h4>
                  <p className="text-slate-400 text-xs mb-3">{sec.worked_example.data}</p>
                  <div className="space-y-1 mb-3">
                    {sec.worked_example.steps?.map((s, si2) => (
                      <div key={si2} className="text-sm text-slate-300 pl-4 border-l-2 border-emerald-800/40 py-0.5" dangerouslySetInnerHTML={{ __html: s }} />
                    ))}
                  </div>
                  <div className="bg-[#0A1628] rounded p-3 text-sm text-emerald-300 border border-emerald-800/20">{sec.worked_example.conclusion}</div>
                </div>
              )}

              {sec.auditor_tips?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Auditor Tips</span>
                  </div>
                  <div className="space-y-2">
                    {sec.auditor_tips.map((tip, ti) => (
                      <div key={ti} className="bg-amber-900/10 border border-amber-800/20 rounded px-4 py-3 text-sm text-amber-200/90 leading-relaxed">{tip}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Watermark */}
          <div className="text-center text-[10px] text-slate-600 py-4 border-t border-[#1E3A5F] mt-8" data-testid="reader-watermark">
            {chapter.watermark}
          </div>

          {/* Chapter navigation */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1E3A5F]">
            {chapter.has_prev ? (
              <button onClick={() => readChapter(chapter.book_id, chapter.chapter_num - 1)} className="flex items-center gap-2 text-[#00C8E0] hover:text-white text-sm" data-testid="prev-chapter-btn">
                <ChevronLeft className="w-4 h-4" /> Previous Chapter
              </button>
            ) : <div />}
            {chapter.has_next ? (
              <button onClick={() => readChapter(chapter.book_id, chapter.chapter_num + 1)} className="flex items-center gap-2 text-[#00C8E0] hover:text-white text-sm" data-testid="next-chapter-btn">
                Next Chapter <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => setView(VIEW_BOOK)} className="flex items-center gap-2 text-emerald-400 hover:text-white text-sm" data-testid="back-to-toc-btn">
                Back to Contents <List className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ SERIES I/II BOOK VIEW — Table of Contents ═══ */
  if (view === VIEW_BOOK && selectedBook) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="reader-book-toc">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button onClick={() => { setSelectedBook(null); setView(VIEW_HOME); }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> My Books
          </button>
          <div className="text-xs text-slate-500">{selectedBook.chapter_count} chapters</div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="text-[10px] text-[#00C8E0] uppercase tracking-widest font-bold mb-2">Brain Drain Series</div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="book-title">{selectedBook.title}</h1>
            <p className="text-slate-400 text-sm">{selectedBook.subtitle}</p>
            <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
              <span>{selectedBook.author}</span>
              <span className="text-emerald-400 font-bold">{selectedBook.progress_pct}% complete</span>
            </div>
          </div>

          <div className="h-1 bg-[#1E3A5F] rounded-full mb-8 overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${selectedBook.progress_pct}%` }} />
          </div>

          <div className="space-y-2" data-testid="chapters-list">
            {selectedBook.chapters.map((ch) => {
              const isCompleted = selectedBook.completed_chapters?.includes(ch.chapter_num);
              const isBookmarked = selectedBook.bookmarks?.includes(ch.chapter_num);

              return (
                <button
                  key={ch.chapter_num}
                  onClick={() => readChapter(selectedBook.book_id, ch.chapter_num)}
                  disabled={chapterLoading}
                  className="w-full text-left bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-lg p-4 hover:border-[#1A6BCC]/40 transition-colors group flex items-center gap-4 disabled:opacity-50"
                  data-testid={`chapter-${ch.chapter_num}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isCompleted ? "bg-emerald-600 text-white" : "bg-[#132340] text-slate-400"}`}>
                    {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : ch.chapter_num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors truncate">{ch.title}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                      <span>{ch.category}</span>
                      {ch.is_bonus && <span className="text-emerald-400 font-bold">FREE BONUS</span>}
                      {isBookmarked && <BookmarkCheck className="w-3 h-3 text-amber-400" />}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#00C8E0] shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ SEARCH VIEW ═══ */
  if (view === VIEW_SEARCH) {
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="reader-search-view">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center gap-4">
          <button onClick={() => setView(VIEW_HOME)} className="text-slate-400 hover:text-white" data-testid="search-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search all content..."
              className="flex-1 bg-[#0D1F38] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none"
              data-testid="search-input-top"
            />
            <button onClick={doSearch} disabled={searching} className="bg-[#1A6BCC] px-4 rounded text-sm font-bold" data-testid="search-go">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-6">
          <p className="text-xs text-slate-500 mb-4">{searchResults.length} results for "{searchQuery}"</p>
          <div className="space-y-2">
            {searchResults.map((r, i) => (
              <button
                key={i}
                onClick={() => {
                  if (r.is_series_iii && r.node_id) {
                    // Set S3 context and read node
                    const s3 = dashboard?.books?.find(b => b.is_series_iii);
                    if (s3) {
                      setS3Book(s3);
                      setS3Volume(r.volume || 1);
                      readS3Node(r.node_id);
                    }
                  } else {
                    readChapter(r.book_id, r.chapter_num);
                  }
                }}
                className="w-full text-left bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-lg p-4 hover:border-[#1A6BCC]/40 transition-colors"
                data-testid={`search-result-${i}`}
              >
                <div className="text-xs text-[#00C8E0] mb-1" data-testid={`search-result-part-${i}`}>
                  {r.is_series_iii
                    ? (() => {
                        // v11 Architecture migration (Items 6-7): render
                        // "Series III · Part I/II/III" instead of the legacy
                        // "Vol 1-4" / `volume_title` strings. Falls back to
                        // the legacy label when the architecture lookup fails.
                        const part = resolvePartFromArch(v11Arch, r);
                        const partLabel = part
                          ? shortPartLabel(part)
                          : (r.volume_title || `Vol ${r.volume}`);
                        return `Series III · ${partLabel}`;
                      })()
                    : `${r.book_title} · Ch. ${r.chapter_num}`
                  }
                </div>
                <div className="text-sm font-bold text-white mb-1">{r.chapter_title || r.section_title}</div>
                {r.match_context && <p className="text-xs text-slate-400 line-clamp-2">{r.match_context}</p>}
              </button>
            ))}
            {searchResults.length === 0 && (
              <div className="text-center text-slate-500 py-12 text-sm">No results found. Try different keywords.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ HOME VIEW — My Books ═══ */
  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="reader-dashboard-home">
      {/* Top bar */}
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#1A6BCC] rounded flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-sm">Reader Hub</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search content..."
              className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-3 py-1.5 text-xs text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none w-48"
              data-testid="search-input"
            />
            <button onClick={doSearch} disabled={searching} className="bg-[#132340] border border-[#1E3A5F] rounded px-2.5 py-1.5 text-slate-400 hover:text-white" data-testid="search-btn">
              <Search className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-xs text-slate-500">{dashboard?.reader_name}</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Welcome */}
        {justActivated && (
          <div className="bg-emerald-900/20 border border-emerald-800/30 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="welcome-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div>
              <div className="text-sm font-bold text-emerald-300">Welcome to the Reader Hub!</div>
              <p className="text-xs text-emerald-400/70">Your access has been activated. Enjoy the full digital edition.</p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1" data-testid="dashboard-title">My Library</h1>
          <p className="text-slate-400 text-sm">{dashboard?.total_books || 0} book{(dashboard?.total_books || 0) !== 1 ? "s" : ""} in your collection</p>
        </div>

        {/* Books */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="books-grid">
          {dashboard?.books?.map((book) => (
            <button
              key={book.book_id}
              onClick={() => openBook(book)}
              className="text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg overflow-hidden hover:border-[#1A6BCC]/40 transition-colors group"
              data-testid={`book-card-${book.book_id}`}
            >
              {/* Cover area */}
              <div className="h-48 bg-[#132340] flex items-center justify-center relative overflow-hidden">
                {book.cover_type === "image" && book.cover_image ? (
                  <img src={book.cover_image} alt={book.title} className="h-full w-full object-contain" />
                ) : (
                  <div className="text-center px-6">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">
                      {book.is_series_iii ? "Brain Drain Series - III" : "Brain Drain Series"}
                    </div>
                    <div className="text-sm font-bold text-white">{book.title}</div>
                    {book.is_series_iii && (
                      <div className="text-[10px] text-[#00C8E0] mt-2">{book.part_count ?? 3} Parts</div>
                    )}
                  </div>
                )}
                {/* Progress overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#1E3A5F]">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${book.progress_pct}%` }} />
                </div>
              </div>

              <div className="p-4">
                <h3 className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors mb-1">{book.title}</h3>
                <p className="text-xs text-slate-400 mb-3">{book.subtitle}</p>
                <div className="flex items-center justify-between text-[10px] text-slate-500">
                  {book.is_series_iii ? (
                    <span>{book.part_count ?? 3} Parts · {book.chapter_count} Chapters</span>
                  ) : (
                    <span>{book.chapter_count} chapters</span>
                  )}
                  <span className="text-emerald-400 font-bold">{book.progress_pct}%</span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {(!dashboard?.books || dashboard.books.length === 0) && (
          <div className="text-center py-16" data-testid="no-books">
            <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-400 mb-2">No books yet</h2>
            <p className="text-sm text-slate-500 mb-4">Activate an access code from your print book to get started.</p>
            <button onClick={() => navigate("/readers")} className="bg-[#1A6BCC] text-white px-6 py-2 rounded text-sm font-bold hover:bg-[#1A6BCC]/80" data-testid="activate-code-btn">
              Activate Code
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-6 border-t border-[#1E3A5F]/50">
          <p className="text-slate-600 text-[10px]">
            Reader Edition · Brain Drain Series · Charles Muchina · CHARSTAN Intelligence Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReaderDashboardPage;

// ---------------------------------------------------------------------------
// v11 Part-architecture subcomponents
// ---------------------------------------------------------------------------

const PartSection = ({ part, chapterLabel, onChapterClick }) => (
  <section
    className="border border-[#1E3A5F]/60 rounded-lg overflow-hidden"
    data-testid={`s3-part-${part.part}`}
  >
    <header className="bg-gradient-to-r from-[#1A6BCC]/15 to-[#00C8E0]/5 border-b border-[#1E3A5F]/60 px-5 py-4">
      <h2 className="text-base font-bold text-white mb-1.5">{part.label}</h2>
      <p className="text-[11px] text-slate-400 leading-relaxed">{part.subtitle}</p>
      <div className="mt-2 text-[10px] text-[#00C8E0] uppercase tracking-wider">
        {part.chapter_count} {part.chapter_count === 1 ? "chapter" : "chapters"}
      </div>
    </header>
    <ul className="divide-y divide-[#1E3A5F]/40">
      {part.chapters.map((c) => (
        <li key={c.node_id}>
          <button
            onClick={() => onChapterClick(c.node_id)}
            className="w-full text-left px-5 py-3 hover:bg-[#132340]/60 transition-colors flex items-center gap-3 group"
            data-testid={`s3-chapter-link-ch${c.chapter}`}
          >
            <div className="w-9 h-9 rounded bg-[#0A1628] border border-[#1E3A5F] flex items-center justify-center text-[11px] font-bold text-[#00C8E0] shrink-0 group-hover:border-[#1A6BCC]/60">
              {c.chapter}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white group-hover:text-[#00C8E0] transition-colors truncate">
                {c.chapter_title}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {chapterLabel} {c.chapter}
              </div>
            </div>
            <span className="text-slate-600 group-hover:text-[#00C8E0] text-xs">→</span>
          </button>
        </li>
      ))}
    </ul>
  </section>
);

const BoundaryMatterCard = ({ node, label, onClick, testid }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-[#0D1F38] border border-[#1E3A5F]/50 border-dashed rounded-lg px-5 py-3 hover:border-[#1A6BCC]/40 transition-colors"
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded bg-[#132340] flex items-center justify-center shrink-0">
        <span className="text-[10px] text-slate-400">✎</span>
      </div>
      <div className="flex-1">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-slate-200">{node.chapter_title || node.node_id}</div>
      </div>
    </div>
  </button>
);
